import { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TIconProps } from '@/common/components/Icons/Icon';
import {
  benefitRouteTypeMapping,
  getPercentageOfUsage,
} from '@/common/utils/benefits/benefit-utils';
import { DateUtils } from '@/common/utils/date';
import {
  Card,
  IconBadge,
  IconCircleInfoRegular,
  IconIcons,
  IconPiggyBank,
  ProgressBar,
  Text,
  Tooltip,
  themeVars,
} from '@coverflex-tech/hypernova';
import { MoneyUtils, TMoney } from '@coverflex-tech/hypernova-utils';
import { css } from './UsageCard.css';

interface IProps {
  usage: {
    available: TMoney;
    limit: TMoney;
    renewalDate: string;
    carryover?: boolean;
    endsOn: string;
  };
  slug: string;
}

export function UsageCard({ usage, slug }: IProps) {
  const { t } = useTranslation();

  const actualPercentage = getPercentageOfUsage(usage.available, usage.limit);

  const iconMap: Record<string, ReactElement<TIconProps>> = {
    pension_funds: <IconPiggyBank />,
    fringe: <IconIcons />,
  };

  const icon = iconMap[benefitRouteTypeMapping[slug]!] ?? null;

  const hideProgressBar = usage.available.amount === 0 && slug === 'fringe';

  return (
    <Card variant="new" plain>
      <div className={css.containerTitle}>
        {icon && <IconBadge icon={icon} ratio={32} className={css.iconBadge} />}
        <Text bold size="lg">
          {usage.carryover
            ? t('dashboard.welfare.usage_card.carryover.title', {
                year: usage.endsOn.slice(0, 4),
              })
            : t('dashboard.welfare.usage_card.title')}
        </Text>
        {usage.carryover && (
          <Tooltip
            content={t('dashboard.welfare.usage_card.carryover.tooltip')}
            position="top"
          >
            <span className={css.infoIcon}>
              <IconCircleInfoRegular />
            </span>
          </Tooltip>
        )}
      </div>
      {hideProgressBar ? (
        <div className={css.containerReachedLimit}>
          <Text size="md">
            {t('dashboard.fringe.usage_card.reached', {
              amount: MoneyUtils.moneyToString(usage.limit),
            })}
          </Text>
        </div>
      ) : (
        <>
          <ProgressBar
            bars={[
              {
                percentage: actualPercentage,
                color: themeVars.color.semantic.surface.accentSecondary40,
              },
            ]}
          />
          <div className={css.containerBudget}>
            <Text data-test-id="budget-available">
              <Trans
                i18nKey={'dashboard.welfare.usage_card.how_much'}
                values={{
                  amount: MoneyUtils.moneyToString(usage.available),
                }}
              />
              {slug === 'fringe' && (
                <Tooltip
                  content={t('dashboard.fringe.usage_card.tooltip')}
                  position="right"
                >
                  <span className={css.infoIcon}>
                    <IconCircleInfoRegular />
                  </span>
                </Tooltip>
              )}
            </Text>

            <Text data-test-id="budget-limit">
              {t('dashboard.welfare.usage_card.limit', {
                amount: MoneyUtils.moneyToString(usage.limit),
              })}
            </Text>
          </div>
        </>
      )}

      {!usage.carryover && (
        <div>
          <Text
            size="sm"
            color="neutral20"
            className={css.textRenewal}
            data-test-id="renewal-date"
          >
            {t('dashboard.welfare.usage_card.renewal_date')}
          </Text>
          <Text size="md" bold>
            {DateUtils.formatDate(usage.renewalDate)}
          </Text>
        </div>
      )}
    </Card>
  );
}
